// Watermark placeholders shared by the link settings preview and the public viewer,
// so what the owner previews is exactly what a viewer sees.
export const DEFAULT_WATERMARK = '{{email}} {{ip-address}} {{date}} {{time}}';
export const WATERMARK_PLACEHOLDERS = ['{{email}}', '{{ip-address}}', '{{date}}', '{{time}}'];

// Visual stamping config, separate from the text template above - mirrors
// express-server/services/document-service.js's WATERMARK_OPTION_DEFAULTS
// and parseWatermarkOptions (keep both in sync). Matches the look stampPdf
// always used before this was configurable, so an existing link with no
// watermarkOptions saved yet renders identically to before.
// Mirrors WATERMARK_POSITIONS in document-service.js - keep in sync.
export type WatermarkPosition =
    | 'tiled' | 'center'
    | 'top-left' | 'top-center' | 'top-right'
    | 'middle-left' | 'middle-right'
    | 'bottom-left' | 'bottom-center' | 'bottom-right';

export const WATERMARK_POSITION_OPTIONS: { value: WatermarkPosition; label: string }[] = [
    { value: 'tiled', label: 'Tiled across the page' },
    { value: 'center', label: 'Center' },
    { value: 'top-left', label: 'Top Left' },
    { value: 'top-center', label: 'Top Center' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'middle-left', label: 'Middle Left' },
    { value: 'middle-right', label: 'Middle Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom-center', label: 'Bottom Center' },
    { value: 'bottom-right', label: 'Bottom Right' },
];

// Preset choices for the watermark settings selects. A saved value that isn't
// one of these (set before the presets existed) is still shown, as "Custom".
export const WATERMARK_ROTATION_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: 'Do not rotate' },
    { value: 30, label: 'Tilt up (30°)' },
    { value: -30, label: 'Tilt down (-30°)' },
    { value: 45, label: 'Diagonal up (45°)' },
    { value: -45, label: 'Diagonal down (-45°)' },
    { value: 90, label: 'Vertical (90°)' },
];

export const WATERMARK_OPACITY_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: 'No transparency' },
    { value: 0.75, label: '25% transparent' },
    { value: 0.5, label: '50% transparent' },
    { value: 0.25, label: '75% transparent' },
    { value: 0.2, label: '80% transparent' },
    { value: 0.1, label: '90% transparent' },
];

export const WATERMARK_FONT_SIZE_OPTIONS: number[] = [8, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72];
export type WatermarkOptions = {
    position: WatermarkPosition;
    rotation: number;
    opacity: number;
    fontSize: number;
    fontColor: string;
};
export const DEFAULT_WATERMARK_OPTIONS: WatermarkOptions = {
    position: 'tiled',
    rotation: -30,
    opacity: 0.2,
    fontSize: 11,
    fontColor: '#737373',
};

export type WatermarkValues = {
    email?: string | null;
    ip?: string | null;
};

// Fills in a watermark template for one viewer. Anonymous viewers (public links) have
// no email, and their IP is unknown until they load the page, so those are labelled.
export const renderWatermark = (template: string, values: WatermarkValues = {}, now: Date = new Date()): string => {
    const date = now.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    const time = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    return template
        .split('{{email}}').join(values.email || 'Anonymous')
        .split('{{ip-address}}').join(values.ip || 'Unknown IP')
        .split('{{date}}').join(date)
        .split('{{time}}').join(time);
};
